import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Activity, Search, Filter, RefreshCw, Loader2, ChevronLeft, ChevronRight,
  User, Calendar, Clock, FileText, DollarSign, Settings, Shield, Eye,
  AlertTriangle, CheckCircle, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { parseISO, formatDistanceToNow } from 'date-fns';

// Event type to human-readable label mapping
const EVENT_TYPE_LABELS = {
  // Authentication
  LOGIN_SUCCESS:   { label: 'Login',              icon: User,          color: 'text-green-600 bg-green-100' },
  LOGIN_FAILED:    { label: 'Failed Login',       icon: AlertTriangle, color: 'text-red-600 bg-red-100' },
  LOGOUT:          { label: 'Logout',             icon: User,          color: 'text-slate-600 bg-slate-100' },
  TOKEN_REFRESH:   { label: 'Token Refresh',      icon: Shield,        color: 'text-blue-600 bg-blue-100' },
  PASSWORD_CHANGED:{ label: 'Password Changed',   icon: Shield,        color: 'text-amber-600 bg-amber-100' },
  PASSWORD_RESET:  { label: 'Password Reset',     icon: Shield,        color: 'text-amber-600 bg-amber-100' },

  // User Management
  USER_CREATED:       { label: 'User Created',       icon: User,     color: 'text-blue-600 bg-blue-100' },
  USER_UPDATED:       { label: 'User Updated',       icon: Settings, color: 'text-yellow-600 bg-yellow-100' },
  USER_DELETED:       { label: 'User Deleted',       icon: XCircle,  color: 'text-red-600 bg-red-100' },
  USER_ACCESS_CHANGED:{ label: 'User Access Changed', icon: Shield,  color: 'text-purple-600 bg-purple-100' },

  // Campaign Management
  CAMPAIGN_CREATED:    { label: 'Event Created',    icon: FileText, color: 'text-purple-600 bg-purple-100' },
  CAMPAIGN_UPDATED:    { label: 'Event Updated',    icon: FileText, color: 'text-blue-600 bg-blue-100' },
  CAMPAIGN_DELETED:    { label: 'Event Deleted',    icon: XCircle,  color: 'text-red-600 bg-red-100' },
  CAMPAIGN_ARCHIVED:   { label: 'Event Archived',   icon: FileText, color: 'text-slate-600 bg-slate-100' },
  CAMPAIGN_UNARCHIVED: { label: 'Event Unarchived', icon: FileText, color: 'text-green-600 bg-green-100' },

  // Landing Page
  LANDING_PAGE_CREATED:  { label: 'Landing Page Created',   icon: FileText,   color: 'text-indigo-600 bg-indigo-100' },
  LANDING_PAGE_UPDATED:  { label: 'Landing Page Updated',   icon: FileText,   color: 'text-indigo-600 bg-indigo-100' },
  LANDING_PAGE_PUBLISHED:{ label: 'Landing Page Published', icon: CheckCircle, color: 'text-green-600 bg-green-100' },
  LANDING_PAGE_DELETED:  { label: 'Landing Page Deleted',   icon: XCircle,    color: 'text-red-600 bg-red-100' },

  // Registrations
  REGISTRATION_CREATED:  { label: 'Registration Created',   icon: User,     color: 'text-green-600 bg-green-100' },
  REGISTRATION_UPDATED:  { label: 'Registration Updated',   icon: Settings, color: 'text-blue-600 bg-blue-100' },
  REGISTRATION_CANCELLED:{ label: 'Registration Cancelled', icon: XCircle,  color: 'text-red-600 bg-red-100' },

  // Payments
  PAYMENT_INITIATED: { label: 'Payment Initiated', icon: DollarSign,  color: 'text-blue-600 bg-blue-100' },
  PAYMENT_VERIFIED:  { label: 'Payment Verified',  icon: CheckCircle, color: 'text-green-600 bg-green-100' },
  PAYMENT_FAILED:    { label: 'Payment Failed',    icon: XCircle,     color: 'text-red-600 bg-red-100' },
  PAYMENT_WEBHOOK:   { label: 'Payment Webhook',   icon: DollarSign,  color: 'text-blue-600 bg-blue-100' },
  REFUND_INITIATED:  { label: 'Refund Initiated',  icon: DollarSign,  color: 'text-amber-600 bg-amber-100' },
  REFUND_COMPLETED:  { label: 'Refund Completed',  icon: DollarSign,  color: 'text-green-600 bg-green-100' },

  // Fund Transfers (backend uses FUND_TRANSFER_* constants)
  FUND_TRANSFER_REQUESTED: { label: 'Transfer Requested', icon: DollarSign,  color: 'text-blue-600 bg-blue-100' },
  FUND_TRANSFER_APPROVED:  { label: 'Transfer Approved',  icon: CheckCircle, color: 'text-green-600 bg-green-100' },
  FUND_TRANSFER_REJECTED:  { label: 'Transfer Rejected',  icon: XCircle,     color: 'text-red-600 bg-red-100' },
  FUND_TRANSFER_COMPLETED: { label: 'Transfer Completed', icon: DollarSign,  color: 'text-green-600 bg-green-100' },

  // Offline Payments / Kiosk
  OFFLINE_REGISTRATION: { label: 'Offline Registration', icon: User,        color: 'text-blue-600 bg-blue-100' },
  OTP_VERIFIED:         { label: 'OTP Verified',         icon: CheckCircle, color: 'text-green-600 bg-green-100' },
  CASH_CONFIRMED:       { label: 'Cash Confirmed',       icon: DollarSign,  color: 'text-green-600 bg-green-100' },
  NO_SHOW:              { label: 'No Show',              icon: XCircle,     color: 'text-red-600 bg-red-100' },
  KIOSK_CHECK_IN:       { label: 'Kiosk Check-in',      icon: CheckCircle, color: 'text-green-600 bg-green-100' },
  KIOSK_REGISTER:       { label: 'Kiosk Registration',  icon: User,        color: 'text-blue-600 bg-blue-100' },

  // Data Operations
  DATA_EXPORTED:    { label: 'Data Exported',     icon: FileText, color: 'text-purple-600 bg-purple-100' },
  MEMBERS_UPLOADED: { label: 'Members Imported',  icon: FileText, color: 'text-purple-600 bg-purple-100' },

  // Form & Settings
  FORM_UPDATED:     { label: 'Form Updated',      icon: FileText, color: 'text-blue-600 bg-blue-100' },
  SYSTEM_CONFIG:    { label: 'System Config',     icon: Settings, color: 'text-slate-600 bg-slate-100' },

  // Change Requests
  CHANGE_REQUEST_CREATED:  { label: 'Change Request Created',  icon: FileText,   color: 'text-blue-600 bg-blue-100' },
  CHANGE_REQUEST_APPROVED: { label: 'Change Request Approved', icon: CheckCircle, color: 'text-green-600 bg-green-100' },
  CHANGE_REQUEST_REJECTED: { label: 'Change Request Rejected', icon: XCircle,     color: 'text-red-600 bg-red-100' },

  // Security
  UNAUTHORIZED_ACCESS: { label: 'Unauthorized Access', icon: Shield, color: 'text-red-600 bg-red-100' },
};

const ActivityLogs = () => {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    event_type: '',
    resource_type: '',
    user_id: '',
    start_date: '',
    end_date: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const pageSize = 50;

  // Check if user has access (Director or Super Admin)
  const hasAccess = isSuperAdmin() || user?.can_approve_transfers;

  useEffect(() => {
    if (!hasAccess) {
      toast.error('Access denied. Directors and Super Admins only.');
      navigate('/dashboard');
      return;
    }
    
    fetchEventTypes();
    fetchStats();
    fetchUsers();
  }, [hasAccess, navigate]);

  useEffect(() => {
    if (hasAccess) {
      fetchLogs();
    }
  }, [page, hasAccess]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await api.get('/activity-logs/event-types');
      setEventTypes(response.data.event_types || []);
    } catch (error) {
      console.error('Failed to fetch event types:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/activity-logs/stats?days=7');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      
      if (filters.event_type) params.append('event_type', filters.event_type);
      if (filters.resource_type) params.append('resource_type', filters.resource_type);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const response = await api.get(`/activity-logs?${params.toString()}`);
      setLogs(response.data.logs || []);
      setTotalLogs(response.data.total);
      setHasMore(response.data.has_more);
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setPage(1);
    fetchLogs();
  };

  const handleClearFilters = () => {
    setFilters({
      event_type: '',
      resource_type: '',
      user_id: '',
      start_date: '',
      end_date: ''
    });
    setPage(1);
    setTimeout(fetchLogs, 100);
  };

  // Backend stores audit timestamps as UTC. With the AppDbContext UTC converter
  // in place, the JSON now carries the 'Z' suffix so JS treats it as UTC. But
  // we still want to *display* IST regardless of the viewer's browser timezone
  // (audit logs are shared across teams in different locations).
  //
  // Use Intl.DateTimeFormat with Asia/Kolkata explicitly so the wall-clock
  // value users see matches the value an auditor in India would see.
  const IST_FORMATTER = new Intl.DateTimeFormat('en-IN', {
    timeZone:    'Asia/Kolkata',
    day:         '2-digit',
    month:       'short',
    year:        'numeric',
    hour:        '2-digit',
    minute:      '2-digit',
    second:      '2-digit',
    hour12:      false,
  });

  // Coerce strings without a 'Z' to UTC, then format.
  const toDateUtcSafe = (raw) => {
    if (!raw) return null;
    // If the string lacks tz info (no 'Z' / '+hh:mm'), treat as UTC.
    const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(raw);
    return parseISO(hasTz ? raw : raw + 'Z');
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = toDateUtcSafe(timestamp);
      return date ? `${IST_FORMATTER.format(date)} IST` : timestamp;
    } catch {
      return timestamp;
    }
  };

  const formatRelativeTime = (timestamp) => {
    try {
      const date = toDateUtcSafe(timestamp);
      return date ? formatDistanceToNow(date, { addSuffix: true }) : '';
    } catch {
      return '';
    }
  };

  const getEventTypeInfo = (eventType) => {
    return EVENT_TYPE_LABELS[eventType] || { 
      label: eventType?.replace(/_/g, ' ') || 'Unknown', 
      icon: Activity, 
      color: 'text-slate-600 bg-slate-100' 
    };
  };

  const renderActionDetails = (details) => {
    if (!details || Object.keys(details).length === 0) return null;
    
    return (
      <div className="mt-2 text-xs text-slate-500">
        {Object.entries(details).map(([key, value]) => {
          if (key === 'password' || value === '[REDACTED]') return null;
          return (
            <span key={key} className="mr-3">
              <span className="text-slate-400">{key.replace(/_/g, ' ')}:</span>{' '}
              <span className="text-slate-600">{Array.isArray(value) ? value.join(', ') : String(value)}</span>
            </span>
          );
        })}
      </div>
    );
  };

  if (!hasAccess) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-slate-900 flex items-center gap-2">
              <Activity className="w-7 h-7 text-primary" />
              Activity Logs
            </h1>
            <p className="text-slate-600 mt-1">
              Track all changes and actions in the system
            </p>
          </div>
          <div className="flex items-center gap-3 mt-4 sm:mt-0">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'bg-primary/10' : ''}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setPage(1); fetchLogs(); fetchStats(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Summary */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4">
              <div className="text-2xl font-bold text-primary">{stats.total_events}</div>
              <div className="text-xs text-slate-500">Events (Last 7 days)</div>
            </Card>
            <Card className="p-4">
              <div className="text-2xl font-bold text-green-600">
                {stats.by_event_type?.find(e => e.event_type === 'LOGIN_SUCCESS')?.count || 0}
              </div>
              <div className="text-xs text-slate-500">Successful Logins</div>
            </Card>
            <Card className="p-4">
              <div className="text-2xl font-bold text-blue-600">
                {(stats.by_event_type?.filter(e => e.event_type?.includes('CAMPAIGN'))?.reduce((a, b) => a + b.count, 0)) || 0}
              </div>
              <div className="text-xs text-slate-500">Event Actions</div>
            </Card>
            <Card className="p-4">
              <div className="text-2xl font-bold text-purple-600">
                {(stats.by_event_type?.filter(e => e.event_type?.includes('TRANSFER'))?.reduce((a, b) => a + b.count, 0)) || 0}
              </div>
              <div className="text-xs text-slate-500">Transfer Actions</div>
            </Card>
          </div>
        )}

        {/* Filters Panel */}
        {showFilters && (
          <Card className="p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <Label className="text-xs">Event Type</Label>
                <Select value={filters.event_type || "all"} onValueChange={(v) => setFilters({...filters, event_type: v === "all" ? "" : v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All events" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All events</SelectItem>
                    {eventTypes.map(type => (
                      <SelectItem key={type} value={type}>
                        {type.replace(/_/g, ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Resource Type</Label>
                <Select value={filters.resource_type || "all"} onValueChange={(v) => setFilters({...filters, resource_type: v === "all" ? "" : v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All resources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All resources</SelectItem>
                    <SelectItem value="campaign">Event</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="fund_transfer">Fund Transfer</SelectItem>
                    <SelectItem value="payment">Payment</SelectItem>
                    <SelectItem value="landing_page">Landing Page</SelectItem>
                    <SelectItem value="registration">Registration</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">User</Label>
                <Select value={filters.user_id || "all"} onValueChange={(v) => setFilters({...filters, user_id: v === "all" ? "" : v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All users</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name || u.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Start Date</Label>
                <Input 
                  type="date" 
                  value={filters.start_date}
                  onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                />
              </div>
              <div>
                <Label className="text-xs">End Date</Label>
                <Input 
                  type="date" 
                  value={filters.end_date}
                  onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                Clear
              </Button>
              <Button size="sm" onClick={handleApplyFilters}>
                Apply Filters
              </Button>
            </div>
          </Card>
        )}

        {/* Logs Table */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No activity logs found</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Event Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Resource
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Details
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      IP Address
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logs.map((log, idx) => {
                    const eventInfo = getEventTypeInfo(log.event_type);
                    const EventIcon = eventInfo.icon;
                    
                    return (
                      <tr key={log.id || idx} className="hover:bg-slate-50">
                        {/* Event Type */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${eventInfo.color}`}>
                              <EventIcon className="w-4 h-4" />
                            </div>
                            <div className="font-medium text-slate-900 text-sm">
                              {eventInfo.label}
                            </div>
                          </div>
                        </td>
                        {/* User (name + email) */}
                        <td className="px-4 py-3">
                          <div className="text-sm text-slate-900">
                            {log.user_name || log.user_email || '-'}
                          </div>
                          {log.user_email && log.user_name && (
                            <div className="text-xs text-slate-400">{log.user_email}</div>
                          )}
                        </td>
                        {/* Resource */}
                        <td className="px-4 py-3">
                          {log.resource_type ? (
                            <div>
                              <div className="text-xs text-slate-500 uppercase">
                                {log.resource_type}
                              </div>
                              <div className="text-sm text-slate-900">
                                {log.resource_name || log.resource_id?.slice(0, 8) || '-'}
                              </div>
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        {/* Details */}
                        <td className="px-4 py-3">
                          {renderActionDetails(log.action_details)}
                        </td>
                        {/* Timestamp */}
                        <td className="px-4 py-3">
                          <div className="text-sm text-slate-900">
                            {formatTimestamp(log.timestamp)}
                          </div>
                          <div className="text-xs text-slate-400">
                            {formatRelativeTime(log.timestamp)}
                          </div>
                        </td>
                        {/* IP Address */}
                        <td className="px-4 py-3">
                          <div className="text-xs text-slate-500">{log.ip_address || '-'}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            log.status === 'success'
                              ? 'bg-green-100 text-green-700'
                              : (log.status === 'failed' || log.status === 'failure' || log.status === 'blocked')
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {log.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          
          {/* Pagination */}
          {!loading && logs.length > 0 && (
            <div className="border-t border-slate-100 px-4 py-3 flex items-center justify-between">
              <div className="text-sm text-slate-500">
                Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalLogs)} of {totalLogs} logs
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-600">Page {page}</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPage(p => p + 1)}
                  disabled={!hasMore}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ActivityLogs;
